import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { residentsApiService } from '../api/residents-api';

// Tipos
export interface BulkUploadResult {
  success: number;
  failed: number;
  errors?: Array<{
    row: number;
    errors: string[];
  }>;
}

export interface BulkUploadJobStatus {
  jobId: string;
  status: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  progress: number;
  result?: BulkUploadResult;
  error?: string;
}

export interface BulkUploadState {
  uploading: boolean;
  downloading: boolean;
  error: string | null;
  result: BulkUploadResult | null;
  currentJobId: string | null;
  jobStatus: BulkUploadJobStatus | null;
}

const initialState: BulkUploadState = {
  uploading: false,
  downloading: false,
  error: null,
  result: null,
  currentJobId: null,
  jobStatus: null,
};

// Async Thunks
export const downloadTemplate = createAsyncThunk(
  'bulkUpload/downloadTemplate',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await residentsApiService.downloadBulkUploadTemplate();
      return blob;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al descargar el template'
      );
    }
  }
);

export const uploadResidentsFile = createAsyncThunk(
  'bulkUpload/uploadResidentsFile',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.uploadBulkResidents(file);
      return response;
    } catch (error: any) {
      // Manejar diferentes tipos de errores
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        return rejectWithValue(
          'No se pudo conectar al servidor. Verifica que el backend esté corriendo en el puerto 3009.'
        );
      }
      
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'El archivo no es válido o tiene un formato incorrecto';
        return rejectWithValue(errorMessage);
      }
      
      if (error.response?.status === 401) {
        return rejectWithValue('No tienes permisos para realizar esta acción');
      }
      
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Error al subir el archivo'
      );
    }
  }
);

export const getBulkUploadStatus = createAsyncThunk(
  'bulkUpload/getBulkUploadStatus',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const response = await residentsApiService.getBulkUploadStatus(jobId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al consultar el estado del job'
      );
    }
  }
);

// Slice
const bulkUploadSlice = createSlice({
  name: 'bulkUpload',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearResult: (state) => {
      state.result = null;
    },
  },
  extraReducers: (builder) => {
    // Download template
    builder
      .addCase(downloadTemplate.pending, (state) => {
        state.downloading = true;
        state.error = null;
      })
      .addCase(downloadTemplate.fulfilled, (state) => {
        state.downloading = false;
        // El blob se maneja en el componente
      })
      .addCase(downloadTemplate.rejected, (state, action) => {
        state.downloading = false;
        state.error = action.payload as string;
      });

    // Upload file
    builder
      .addCase(uploadResidentsFile.pending, (state) => {
        state.uploading = true;
        state.error = null;
        state.result = null;
        state.currentJobId = null;
        state.jobStatus = null;
      })
      .addCase(uploadResidentsFile.fulfilled, (state, action) => {
        state.uploading = false;
        const response = action.payload as { jobId: string; message: string };
        state.currentJobId = response.jobId;
        state.jobStatus = {
          jobId: response.jobId,
          status: 'waiting',
          progress: 0,
        };
      })
      .addCase(uploadResidentsFile.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload as string;
        state.currentJobId = null;
      });

    // Get job status
    builder
      .addCase(getBulkUploadStatus.fulfilled, (state, action) => {
        state.jobStatus = action.payload as BulkUploadJobStatus;
        
        // Si el job está completado, actualizar el resultado
        if (state.jobStatus.status === 'completed' && state.jobStatus.result) {
          state.result = state.jobStatus.result;
        }
        
        // Si el job falló, actualizar el error
        if (state.jobStatus.status === 'failed') {
          state.error = state.jobStatus.error || 'Error al procesar el archivo';
        }
      })
      .addCase(getBulkUploadStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearResult } = bulkUploadSlice.actions;

// Selectors
export const selectBulkUploadUploading = (state: { bulkUpload: BulkUploadState }) =>
  state.bulkUpload.uploading;
export const selectBulkUploadDownloading = (state: { bulkUpload: BulkUploadState }) =>
  state.bulkUpload.downloading;
export const selectBulkUploadError = (state: { bulkUpload: BulkUploadState }) =>
  state.bulkUpload.error;
export const selectBulkUploadResult = (state: { bulkUpload: BulkUploadState }) =>
  state.bulkUpload.result;
export const selectBulkUploadCurrentJobId = (state: { bulkUpload: BulkUploadState }) =>
  state.bulkUpload.currentJobId;
export const selectBulkUploadJobStatus = (state: { bulkUpload: BulkUploadState }) =>
  state.bulkUpload.jobStatus;

export default bulkUploadSlice.reducer;

